import { Hexagon, ImagePlus, MousePointer2, Pin, Route, Tag, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { FileChooser, LinkPicker } from '@/components/admin/tool-animations/scenes-basic';
import { A, Cursor, Rip, Show, TimeProvider, type Css, type Key } from '@/components/admin/tool-animations/engine';
import { Defs } from '@/components/admin/tool-animations/room';
import {
  BoxRing,
  C,
  DraftPoly,
  DraftVertex,
  Glass,
  Handles,
  LinkDot,
  POLY,
  POLY_SEL,
  makeBar,
  path,
  type BarSpec,
  type Pt,
} from '@/components/admin/tool-animations/ui';
import { DiningRoom, LivingRoom } from '@/components/help/scenes/scene-kit';
import '@/components/admin/tool-animations/animations.css';

/**
 * The login / signup panel's animated scene: the drawn living room with the
 * editor working over it. A cursor picks tools in the (redrawn) palette and
 * connects the door to another image, follows that link into the kitchen
 * (a short crossfade, like the editor's image change), traces a polygon on the
 * cabinets, labels the room and places an image, then everything fades and loops.
 *
 * Everything lives in the room's own viewBox (32 32 488 328, slice), so the
 * pieces stay on their furniture at any panel size. The editor pieces are the
 * tool-card ones (256x144 card units, ui.tsx / scenes-basic.tsx) drawn at `S`
 * of their size: one card unit is 0.6 room units, which at the panel's usual
 * 2.7-3.2 px per room unit gives the editor's real pixel sizes.
 *
 * Action stays inside x 175..385, y 62..290: what survives the slice down to
 * a 1024 px wide screen and clears the logo chip (top-left) and the tour card
 * (bottom-left). Motion is the engine's CSS keyframes (no rAF); with reduced
 * motion the inline final frame shows the result and no cursor.
 */

/** Loop length in seconds (the tour card's CSS in auth-showcase.css uses the same). */
export const SHOWCASE_LOOP = 21.5;
const T = SHOWCASE_LOOP;

/** Card units -> room units. */
const S = 0.6;
const at = (x: number, y: number) => `translate(${x} ${y}) scale(${S})`;

/** Second at which the view changes from the living room to the kitchen. */
const GO = 7.1;

/* ---------- where things are (room units) ---------- */

const PAL = { x: 176, y: 86 } as const;
/** Centre of palette button `i`, in card units inside the palette and then in room units. */
const btn = (i: number): Pt => [PAL.x + 15 * S, PAL.y + (14.3 + 24 * i) * S];
const TOOL = { select: 0, point: 1, polygon: 2, label: 3, image: 4 } as const;

const DOOR: Pt = [352, 176];
const PICKER: Pt = [258, 96];
/** The "Cocina" tile of the link picker (first thumbnail). */
const COCINA: Pt = [PICKER[0] + 45 * S, PICKER[1] + 45.5 * S];
/** The "Conectar" row of the palette flyout. */
const CONNECT_ROW: Pt = [PAL.x + 74 * S, PAL.y + 59.5 * S];
/** Kitchen: polygon on the base cabinets, label and image on the wall cabinets. */
const POLY_PTS: readonly Pt[] = [
  [214, 190],
  [334, 190],
  [334, 250],
  [214, 250],
];
const NAME: Pt = [226, 134];
const IMG: Pt = [332, 134];
/** The system file chooser, centred in the room (card units 34..222 x 16..112 -> room). */
const CHOOSER: Pt = [276 - S * 128, 172 - S * 64];
const inChooser = (x: number, y: number): Pt => [CHOOSER[0] + S * x, CHOOSER[1] + S * y];
const FILE: Pt = inChooser(148, 50);
const OPEN: Pt = inChooser(199, 97);

/* ---------- editor pieces ---------- */

const PAL_TOOLS: readonly LucideIcon[] = [MousePointer2, Pin, Hexagon, Tag, ImagePlus];

/** Glyph of palette button `i` (card units), light or dark (on the active ink tile). */
function Glyph({ i, dark, icon }: { i: number; dark?: boolean; icon?: LucideIcon }) {
  const Icon = icon ?? PAL_TOOLS[i] ?? Pin;
  return <Icon x={15 - 5} y={14.3 + 24 * i - 5} width={10} height={10} stroke={dark ? '#0d0e0f' : C.ink2} strokeWidth={2} fill="none" />;
}

const Tile = ({ i }: { i: number }) => <rect x="4" y={3.3 + 24 * i} width="22" height="22" rx="6.6" fill={C.ink} />;

/** Palette button `i` is the active tool during [a, b] (or from `a` to the end). */
function Active({ i, a, b, icon, final }: { i: number; a: number; b?: number; icon?: LucideIcon; final?: boolean }) {
  return (
    <Show a={a} b={b} final={final}>
      <Tile i={i} />
      <Glyph i={i} dark icon={icon} />
    </Show>
  );
}

function Palette() {
  return (
    <g transform={at(PAL.x, PAL.y)}>
      <Glass x={0} y={0} width={30} height={125} rx={6.6} />
      {PAL_TOOLS.map((_, i) => (
        <Glyph key={i} i={i} />
      ))}
      {/* select: active at the start, after the link, and again at the end */}
      <Active i={TOOL.select} a={0} b={1.4} final={false} />
      <Active i={TOOL.select} a={5.7} b={8.3} final={false} />
      <Active i={TOOL.select} a={20.2} />
      {/* point -> Conectar */}
      <Show a={1.4} b={5.7} final={false}>
        <Tile i={TOOL.point} />
      </Show>
      <Show a={1.4} b={2.5} final={false}>
        <Glyph i={TOOL.point} dark />
      </Show>
      <Show a={2.5} b={5.7} final={false}>
        <Glyph i={TOOL.point} dark icon={Route} />
      </Show>
      <Active i={TOOL.polygon} a={8.3} b={11.7} final={false} />
      <Active i={TOOL.label} a={12.4} b={15.8} final={false} />
      <Active i={TOOL.image} a={15.8} b={20.2} final={false} />
      {/* corner triangles of the grouped tools (select, point, polygon) */}
      <path d="M27 24 L27 21 L24 24 Z M27 48 L27 45 L24 48 Z M27 72 L27 69 L24 72 Z" fill={C.ink3} opacity=".7" />
    </g>
  );
}

/** The palette's variants flyout (menu on black glass): Punto, Conectar. */
function Flyout() {
  return (
    <g transform={at(PAL.x, PAL.y)}>
      <g filter="url(#ta-pop)">
        <Glass x={34} y={25} width={80} height={48} rx={6} />
      </g>
      <Show a={0} b={2.3} final={false}>
        <rect x="38" y="29" width="72" height="19" rx="4" fill="rgba(255,255,255,.08)" />
      </Show>
      <Show a={2.25} final={false}>
        <rect x="38" y="50" width="72" height="19" rx="4" fill="rgba(255,255,255,.08)" />
      </Show>
      <Pin x={43} y={33} width={9} height={9} stroke={C.ink} strokeWidth={2} fill="none" />
      <text x="56" y="42.2" fontSize="7.4" fill={C.ink}>
        Punto
      </text>
      <Route x={43} y={54} width={9} height={9} stroke={C.ink} strokeWidth={2} fill="none" />
      <text x="56" y="63.2" fontSize="7.4" fill={C.ink}>
        Conectar
      </text>
    </g>
  );
}

/** A tool-card bar, centred on (cx, cy) of the room. */
function Bar({ bar, cx, cy, children }: { bar: BarSpec; cx: number; cy: number; children?: ReactNode }) {
  const x = cx - S * (bar.x + bar.w / 2);
  const y = cy - S * (bar.y + bar.h / 2);
  return (
    <g transform={`translate(${x} ${y}) scale(${S})`}>
      {bar.el}
      {children}
    </g>
  );
}

/** Where item `i` of row 0 of `bar` ends up in the room (when the bar is centred on cx, cy). */
function itemAt(bar: BarSpec, i: number, cx: number, cy: number): Pt {
  const [x, y] = bar.at(0, i);
  return [cx + S * (x - bar.x - bar.w / 2), cy + S * (y - bar.y - bar.h / 2)];
}

const TOP_Y = 70;
const BOTTOM_Y = 281;
const CX = 276;

const hint = (s: string) => ({ t: 'text', s }) as const;
const sep = { t: 'sep' } as const;
const status = (s: string) => ({ t: 'status', s }) as const;

const BARS = {
  connect: makeBar([[hint('Haz clic donde se pasa a otra imagen: una puerta, el piso frente…')]], 'top'),
  poly0: makeBar([[hint('Haz clic para agregar puntos')]], 'top'),
  poly1: makeBar([[hint('Haz clic para agregar puntos'), sep, status('Faltan 2 puntos')]], 'top'),
  poly2: makeBar([[hint('Haz clic para agregar puntos'), sep, status('Falta 1 punto')]], 'top'),
  create: makeBar([[{ t: 'btn', s: 'Cancelar' }, { t: 'btn', s: 'Crear polígono', icon: 'check', kbd: 'Enter', primary: true }]], 'bottom'),
  image: makeBar([[hint('Haz clic donde quieras colocar la imagen')]], 'top'),
};

/* ---------- label (the editor's default look: white pill, black text) ---------- */

const LABEL_W = 29; // "Cocina" at 9 px, like the tool card's
const LABEL_TEXT = { y: 0, textAnchor: 'middle', dominantBaseline: 'central', fontSize: 9, fill: '#000000' } as const;
/** Place click, then the in-place edit: select all, type the name (one letter per step), done. */
const PLACE = 13.3;
const TYPED = [14.4, 14.55, 14.7, 14.85, 15.0, 15.15];
const NAMED = 15.3;

/** Hard steps: the value changes at each `at[k]`. */
function stepped(times: readonly number[], values: readonly Css[], from: Css): Key[] {
  const keys: Key[] = [[0, from]];
  times.forEach((t, k) => {
    keys.push([t - 0.01, k === 0 ? from : (values[k - 1] ?? from)], [t, values[k] ?? from]);
  });
  return keys;
}

function Pill({ w }: { w: number }) {
  return (
    <g filter="url(#ta-pop)">
      <rect x={-w / 2} y={-9} width={w} height="18" rx="5" fill="#ffffff" />
    </g>
  );
}

function Label() {
  const clip = TYPED.map((_, k): Css => ({ clipPath: `inset(0 ${((1 - (k + 1) / 6) * 100).toFixed(1)}% 0 0)` }));
  const shift = TYPED.map((_, k): Css => ({ transform: `translateX(${((LABEL_W - (LABEL_W * (k + 1)) / 6) / 2).toFixed(2)}px)` }));
  const caret = TYPED.map((_, k): Css => ({ transform: `translateX(${((LABEL_W * (k + 1)) / 6 - LABEL_W / 2 + 1).toFixed(2)}px)` }));
  return (
    <g transform={at(NAME[0], NAME[1])}>
      {/* placed with its default text, then edited in place */}
      <Show a={PLACE + 0.1} b={NAMED + 0.1} final={false}>
        <Pill w={68} />
        <BoxRing x={-34} y={-9} w={68} h={18} />
      </Show>
      <Show a={PLACE + 0.1} b={TYPED[0]! - 0.1} final={false}>
        <text x="0" {...LABEL_TEXT}>
          Nuevo label
        </text>
      </Show>
      <Show a={TYPED[0]! - 0.2} b={TYPED[0]! - 0.05} final={false}>
        <rect x="-26" y="-6.5" width="52" height="13" fill="rgba(61,214,199,.45)" />
        <text x="0" {...LABEL_TEXT}>
          Nuevo label
        </text>
      </Show>
      <Show a={TYPED[0]! - 0.05} b={NAMED + 0.1} final={false}>
        <A keys={stepped(TYPED, shift, { transform: 'translateX(14.5px)' })} final={{ transform: 'translateX(0px)' }}>
          <A keys={stepped(TYPED, clip, { clipPath: 'inset(0 100% 0 0)' })} final={{ clipPath: 'inset(0 0% 0 0)' }} extra={{ transformBox: 'fill-box' }}>
            <text x="0" {...LABEL_TEXT}>
              Cocina
            </text>
          </A>
        </A>
        <A keys={stepped(TYPED, caret, { transform: 'translateX(-14px)' })} final={{ transform: 'translateX(14px)' }}>
          <rect x="0" y="-5.5" width="0.9" height="11" fill="#000000" />
        </A>
      </Show>
      {/* the result */}
      <Show a={NAMED + 0.1}>
        <Pill w={LABEL_W + 14} />
        <text x="0" {...LABEL_TEXT}>
          Cocina
        </text>
      </Show>
    </g>
  );
}

/* ---------- polygon ---------- */

const [P1, P2, P3, P4] = POLY_PTS as [Pt, Pt, Pt, Pt];
const TRACE = [9.0, 9.45, 9.9, 10.35] as const;
const CREATE_AT = 11.2;

/**
 * Like the editor's draft: dots only until the third point, then the closed
 * dashed outline (closing edge included); "Crear polígono" makes it solid.
 */
function Polygon() {
  const vertices = [P1, P2, P3, P4];
  return (
    <g>
      <Show a={TRACE[2] - 0.05} b={TRACE[3] - 0.05} final={false}>
        <DraftPoly pts={[P1, P2, P3]} sw={1} />
      </Show>
      <Show a={TRACE[3] - 0.05} b={CREATE_AT} final={false}>
        <DraftPoly pts={POLY_PTS} sw={1} />
      </Show>
      {vertices.map(([x, y], k) => (
        <Show key={k} a={TRACE[k] ?? 0} b={CREATE_AT} final={false}>
          <g transform={`translate(${x} ${y}) scale(.7)`}>
            <DraftVertex x={0} y={0} />
          </g>
        </Show>
      ))}
      {/* created: selected for a moment, then at rest; all four edges solid */}
      <Show a={CREATE_AT + 0.05}>
        <path d={path(POLY_PTS)} fill={POLY} stroke={C.blue} strokeWidth="1" strokeLinejoin="round" />
      </Show>
      <Show a={CREATE_AT + 0.05} b={CREATE_AT + 1.1} final={false}>
        <path d={path(POLY_PTS)} fill={POLY_SEL} stroke="#ffffff" strokeWidth="1" strokeLinejoin="round" />
        <Handles pts={POLY_PTS} />
      </Show>
    </g>
  );
}

/* ---------- image (placed with the system file chooser) ---------- */

const IMG_PLACE = 16.6;
const IMG_PICK = 18.3;
const IMG_OPEN = 19.6;
const IMG_DONE = 20.0;

function PlacedImage() {
  return (
    <g transform={at(IMG[0], IMG[1])}>
      <Show a={IMG_DONE}>
        <g transform="translate(-22 -15)">
          <rect width="44" height="30" rx="2" fill="#bcdcf2" stroke="#ffffff" strokeWidth="1.6" />
          <path d="M1 29 Q12 15 24 23 T43 19 V29 H1 Z" fill="#7ea67a" />
          <circle cx="32" cy="9" r="4" fill="#fff1b8" />
        </g>
        <BoxRing x={-22} y={-15} w={44} h={30} r={2} />
      </Show>
    </g>
  );
}

/* ---------- the scene ---------- */

/** Click ripples of the cursor (the editor's turquoise, thin: 1 unit is 3 px here). */
const CLICKS: readonly (readonly [number, Pt])[] = [
  [1.4, btn(TOOL.point)],
  [2.5, CONNECT_ROW],
  [3.8, DOOR],
  [4.8, COCINA],
  [5.7, btn(TOOL.select)],
  [6.9, DOOR],
  [8.3, btn(TOOL.polygon)],
  [TRACE[0], P1],
  [TRACE[1], P2],
  [TRACE[2], P3],
  [TRACE[3], P4],
  [CREATE_AT, [CX, BOTTOM_Y]],
  [12.4, btn(TOOL.label)],
  [PLACE, NAME],
  [14.1, NAME],
  [15.8, btn(TOOL.image)],
  [IMG_PLACE, IMG],
  [IMG_PICK - 0.1, FILE],
  [IMG_OPEN, OPEN],
];

function Content() {
  const create = itemAt(BARS.create, 1, CX, BOTTOM_Y);
  const living: Key[] = [
    [0, { opacity: 1 }],
    [GO + 0.4, { opacity: 1 }],
    [GO + 0.5, { opacity: 0 }],
    [T - 0.5, { opacity: 0 }],
    [T - 0.49, { opacity: 1 }],
    [T, { opacity: 1 }],
  ];
  return (
    <>
      {/* living room, then (from GO) the kitchen fades in over it; at the loop's end the kitchen fades out over the living room again */}
      <A keys={living} final={{ opacity: 0 }}>
        <LivingRoom x={32} y={32} w={488} h={328} />
        <g transform={at(DOOR[0], DOOR[1])}>
          <Show a={5.0}>
            <LinkDot x={0} y={0} name="Cocina" />
          </Show>
        </g>
      </A>
      <Show a={GO}>
        <DiningRoom x={32} y={32} w={488} h={328} />
        <Polygon />
        <Label />
        <PlacedImage />
      </Show>

      {/* editor chrome on top */}
      <Palette />
      <Show a={1.5} b={2.8} rise final={false}>
        <Flyout />
      </Show>
      <Show a={2.8} b={5.0} final={false}>
        <Bar bar={BARS.connect} cx={CX} cy={TOP_Y} />
      </Show>
      <Show a={3.9} b={4.95} rise final={false}>
        <g transform={at(PICKER[0], PICKER[1])}>
          <LinkPicker />
        </g>
      </Show>
      <Show a={8.4} b={TRACE[0] + 0.05} final={false}>
        <Bar bar={BARS.poly0} cx={CX} cy={TOP_Y} />
      </Show>
      <Show a={TRACE[0] + 0.05} b={TRACE[1] + 0.05} final={false}>
        <Bar bar={BARS.poly1} cx={CX} cy={TOP_Y} />
      </Show>
      <Show a={TRACE[1] + 0.05} b={TRACE[2] + 0.05} final={false}>
        <Bar bar={BARS.poly2} cx={CX} cy={TOP_Y} />
      </Show>
      <Show a={TRACE[2] + 0.05} b={CREATE_AT + 0.1} rise final={false}>
        <Bar bar={BARS.create} cx={CX} cy={BOTTOM_Y} />
      </Show>
      <Show a={15.9} b={IMG_PLACE + 0.1} final={false}>
        <Bar bar={BARS.image} cx={CX} cy={TOP_Y} />
      </Show>
      <Show a={IMG_PLACE + 0.3} b={IMG_OPEN + 0.1} rise final={false}>
        <g transform={`translate(${CHOOSER[0]} ${CHOOSER[1]}) scale(${S})`}>
          <FileChooser pickAt={IMG_PICK} pickEnd={IMG_OPEN + 0.1} />
        </g>
      </Show>

      {CLICKS.map(([t, [x, y]]) => (
        <Rip key={`${t}-${x}`} t={t} x={x} y={y} r={5} sw={0.9} />
      ))}
      <Cursor
        size={0.42}
        way={[
          [0.3, 340, 246, 0.6],
          [1.2, ...btn(TOOL.point), 0.4],
          [2.1, ...CONNECT_ROW, 0.5],
          [3.6, ...DOOR, 0.4],
          [4.5, ...COCINA, 0.4],
          [5.4, ...btn(TOOL.select), 0.4],
          [6.6, ...DOOR, 0.5],
          [8.0, ...btn(TOOL.polygon), 0.4],
          [TRACE[0] - 0.3, ...P1, 0.3],
          [TRACE[1] - 0.3, ...P2, 0.3],
          [TRACE[2] - 0.3, ...P3, 0.3],
          [TRACE[3] - 0.3, ...P4, 0.3],
          [CREATE_AT - 0.35, ...create, 0.5],
          [12.1, ...btn(TOOL.label), 0.4],
          [PLACE - 0.3, ...NAME, 1.2],
          [15.5, ...btn(TOOL.image), 0.4],
          [IMG_PLACE - 0.3, ...IMG, 0.5],
          [IMG_PICK - 0.4, ...FILE, 0.5],
          [IMG_OPEN - 0.3, ...OPEN, 0.4],
          [20.5, 330, 250, 0.4],
        ]}
        kinds={[
          [0, 'arrow'],
          [2.7, 'cross'],
          [4.1, 'arrow'],
          [8.4, 'cross'],
          [CREATE_AT - 0.5, 'arrow'],
          [12.5, 'cross'],
          [PLACE + 0.3, 'arrow'],
          [15.9, 'cross'],
          [IMG_PLACE + 0.2, 'arrow'],
        ]}
      />
    </>
  );
}

/** The whole scene as an SVG that fills its parent (the room is sliced like a cover image). */
export function ShowcaseScene() {
  return (
    <svg viewBox="32 32 488 328" preserveAspectRatio="xMidYMid slice" fontFamily="inherit" className="block h-full w-full">
      <Defs />
      <TimeProvider value={T}>
        <Content />
      </TimeProvider>
    </svg>
  );
}
